import { Suspense, lazy, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatApiError } from '../../lib/api-error';
import { paiementsApi, type Paiement } from '../../services/api';
import { Spinner } from '../shared/Spinner';

// Lazy comme sur la page de validation des factures (react-pdf est lourd).
const PDFViewer = lazy(() =>
  import('../invoices/PDFViewer').then((m) => ({ default: m.PDFViewer })),
);

/**
 * Panneau latéral droit affichant l'aperçu de la facture d'un paiement.
 * Le PDF est récupéré via GET /api/paiements/:id/pdf (stocké Gertrude → fallback
 * Zoho ~1-2 s). Se ferme par ✕ / backdrop / Échap sans toucher à l'état de la
 * page (sélection préservée). Navigation ◀ ▶ entre paiements sans fermer.
 */
export function PdfPreviewDrawer({
  paiement,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
}: {
  paiement: Paiement;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}) {
  const q = useQuery({
    queryKey: ['paiement-pdf', paiement.id],
    queryFn: () => paiementsApi.pdf(paiement.id),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const navBtn =
    'px-2 py-1 rounded-md text-sm ring-1 ring-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent';

  return (
    <>
      <div className="fixed inset-0 bg-slate-900/40 z-40" onClick={onClose} />
      <aside className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-4 py-2">
          <div className="min-w-0">
            <div className="font-medium truncate">{paiement.vendor_name}</div>
            <div className="text-xs text-slate-500 font-mono truncate">{paiement.bill_number}</div>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className={navBtn} onClick={onPrev} disabled={!hasPrev} title="Facture précédente">
              ◀
            </button>
            <button type="button" className={navBtn} onClick={onNext} disabled={!hasNext} title="Facture suivante">
              ▶
            </button>
            <button
              type="button"
              className="ml-1 px-2 py-1 rounded-md text-sm text-slate-500 hover:bg-slate-100"
              onClick={onClose}
              title="Fermer (Échap)"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {q.isLoading ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-sm text-slate-500">
              <Spinner />
              Récupération du PDF…
            </div>
          ) : q.isError ? (
            <div className="h-full flex items-center justify-center p-6 text-center text-sm text-[#C0423F]">
              {/* `formatApiError` et non `String` : le refus « Cette demande ne vous
                  est pas attribuée » s'affichait préfixé de « ApiError: » (14/10/2026). */}
              Erreur de récupération du PDF : {formatApiError(q.error)}
            </div>
          ) : q.data?.available && q.data.pdf_base64 ? (
            <Suspense
              fallback={
                <div className="h-full flex items-center justify-center text-sm text-slate-500">
                  <Spinner /> Chargement du viewer…
                </div>
              }
            >
              <PDFViewer pdfBase64={q.data.pdf_base64} filename={q.data.filename ?? 'facture.pdf'} />
            </Suspense>
          ) : (
            <div className="h-full flex items-center justify-center p-6 text-center text-sm text-slate-500">
              Aucun justificatif disponible pour cette facture.
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
